/**
 * Draw an SVG element as a PNG — the 3D view, which is SVG, becomes the layout
 * picture an image model follows.
 *
 * Serialised and drawn through an `<img>` onto a canvas, the only route a
 * browser offers. The view's backdrop is a CSS gradient on the element, not
 * part of the SVG, so a solid `background` is painted first — an image model
 * reading edges finds none in transparent pixels, and a transparent layout
 * reads as an empty room.
 */
export interface SvgToPngOptions {
  width: number;
  height: number;
  background?: string;
}

export function svgToPng(svg: SVGSVGElement, options: SvgToPngOptions): Promise<Blob> {
  const { width, height, background = '#ffffff' } = options;
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));
  const markup = new XMLSerializer().serializeToString(clone);
  const url = URL.createObjectURL(new Blob([markup], { type: 'image/svg+xml;charset=utf-8' }));

  return new Promise<Blob>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('This browser cannot draw the 3D view as a picture.');
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (blob) resolve(blob);
          else reject(new Error('The 3D view could not be saved as a picture.'));
        }, 'image/png');
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('The 3D view could not be drawn as a picture.'));
    };
    img.src = url;
  });
}
